import React from "react"
import {
  Box, Flex, List, ListItem, Text,
} from "@chakra-ui/react"
import _ from "lodash"
import { FormatDistanceToken, Locale, formatDistanceStrict } from "date-fns"
import { enUS } from "date-fns/locale"
import { useEventLatestFormSubmissions } from "queries/formSubmissions"
import { FormType, GuestStatus, SubmissionSummary } from "sharedTypes"
import { eventSubmissionsPath } from "utilities/routes"
import { formIconAndType, submissionIconAndType } from "utilities/forms"
import Icon from "components/elements/Icon"
import { asMoney } from "utilities/strings"
import Spinner from "components/elements/Spinner"
import DashboardCard, { EmptyState, LinkAction } from "../components/DashboardCard"
import SubmissionMenu from "../components/SubmissionMenu"

const SHORT_UNITS: Partial<Record<FormatDistanceToken, string>> = {
  xMinutes: "m", xHours: "h", xDays: "d", xMonths: "mo", xYears: "y",
}

const shortLocale: Locale = {
  ...enUS,
  formatDistance: (token, count) => (token === "xSeconds" ? "now" : `${count}${SHORT_UNITS[token]} ago`),
}

export const shortAgo = (at: string, now = new Date()) => (
  formatDistanceStrict(new Date(at), now, { roundingMethod: "floor", locale: shortLocale })
)

const LATEST = 7

const RSVP_ANSWERS: Partial<Record<GuestStatus, string>> = {
  [GuestStatus.Confirmed]: "Yes",
  [GuestStatus.Declined]: "No",
}

const detailOf = ({ price, currency, lineItems }: SubmissionSummary, eventCurrency: string) => {
  const types = _.uniq(lineItems.map(({ form }) => form.type))
  const total = Number(price) ? asMoney(price, currency || eventCurrency) : "Free"

  if (types.includes(FormType.Ticket)) {
    return types.length > 1
      ? total
      : [total, _.uniq(lineItems.map(({ form }) => form.name)).join(", ")].join(" · ")
  }
  if (types.length > 1) return undefined

  switch (types[0]) {
    case FormType.RSVP:
      return lineItems[0].guest ? RSVP_ANSWERS[lineItems[0].guest.status] : undefined
    case FormType.Registration:
      return lineItems.length > 1 ? `+${lineItems.length - 1}` : "Solo"
    default:
      return undefined
  }
}

// Dividers stay inside the row's hover padding, in line with the card's content.
const ROW_STYLE = {
  "&:not(:last-of-type)::before": {
    content: "\"\"", position: "absolute", left: 2, right: 2, bottom: 0, borderBottom: "1px solid", borderColor: "gray.100",
  },
}

type Props = {
  eventId: string
  currency: string
  timeZone: string
  onShareLink: () => void
}

const SubmissionsCard = ({
  eventId, currency, timeZone, onShareLink,
}: Props) => {
  const { data: entries = [], isLoading, isError } = useEventLatestFormSubmissions(eventId, LATEST)

  if (isError) {
    return (
      <DashboardCard title="Recent Submissions">
        <Text color="gray.600">Submissions couldn’t be loaded.</Text>
      </DashboardCard>
    )
  }

  if (isLoading) {
    return (
      <DashboardCard title="Recent Submissions">
        <Spinner />
      </DashboardCard>
    )
  }

  if (!entries.length) {
    return (
      <DashboardCard title="Recent Submissions">
        <EmptyState
          title="No responses yet"
          body="New registrations and RSVPs show up here."
          cta="Share event link"
          onClick={onShareLink}
        />
      </DashboardCard>
    )
  }

  return (
    <DashboardCard title="Recent Submissions">
      <List>
        {entries.map((entry) => {
          const { icon, type: typeName } = _.uniqBy(entry.lineItems, "form.type").length > 1
            ? submissionIconAndType(entry.lineItems)
            : formIconAndType(entry.lineItems[0]?.form.type)
          const detail = detailOf(entry, currency)

          return (
            <ListItem key={entry.externalId} sx={ROW_STYLE} position="relative" display="flex" gap={3} alignItems="flex-start" py={3} px={2} mx={-2} borderRadius="md" _hover={{ bg: "gray.50" }} _focusWithin={{ bg: "gray.50" }} cursor="pointer">
              {icon && <Icon icon={icon} size={4} mt={1} color="gray.600" flexShrink={0} />}
              <Box minW={0} flex={1}>
                <Flex align="baseline" gap={2}>
                  <Text color="gray.600" noOfLines={1} flex={1} minW={0}>
                    {typeName}
                    {detail && <Text as="span" color="gray.900">{` · ${detail}`}</Text>}
                  </Text>
                  <Text fontSize="xs" color="gray.600" whiteSpace="nowrap" flexShrink={0}>{shortAgo(entry.createdAt)}</Text>
                </Flex>
                <Flex minW={0} whiteSpace="pre" overflow="hidden">
                  <SubmissionMenu
                    eventId={eventId}
                    submissionId={entry.externalId}
                    currency={currency}
                    timeZone={timeZone}
                    trigger={(
                      <Box as="button" type="button" color="gray.900" fontWeight="semibold" overflow="hidden" textOverflow="ellipsis" flexShrink={0} maxW="100%" textAlign="left" _focusVisible={{ outline: "none" }} _after={{ content: "\"\"", position: "absolute", inset: 0 }}>
                        {entry.submitterName || "Guest"}
                      </Box>
                    )}
                  />
                  {entry.submitterEmail && (
                    <Text as="span" color="gray.600" overflow="hidden" textOverflow="ellipsis" minW={0}>
                      {` / ${entry.submitterEmail}`}
                    </Text>
                  )}
                </Flex>
              </Box>
            </ListItem>
          )
        })}
      </List>
      <Box mt="auto" pt={4}>
        <LinkAction to={eventSubmissionsPath(eventId)} chevron>View all submissions</LinkAction>
      </Box>
    </DashboardCard>
  )
}

export default SubmissionsCard
